import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone, MessageCircle } from "lucide-react";
import { PHONE_TEL, waLink } from "../lib/contact";

const links = [
  { to: "/", label: "Acasă" },
  { to: "/servicii", label: "Servicii" },
  { to: "/portofoliu", label: "Portofoliu" },
  { to: "/contact", label: "Contact" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <>
      <header
        data-testid="main-navbar"
        className={`fixed inset-x-0 top-0 z-50 transition-[background-color,box-shadow,border-color] duration-500 ${
          scrolled
            ? "border-b border-line/80 bg-cream/85 shadow-[0_8px_30px_-12px_rgba(24,21,18,0.25)] backdrop-blur-xl"
            : "border-b border-transparent bg-transparent"
        }`}
      >
        <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-5 sm:px-8">
          <Link to="/" data-testid="nav-logo" className="group flex items-center">
            <img
              src={scrolled ? "/assets/logo-dark.png" : "/assets/logo-light.png"}
              alt="LOGITEH PROD · Mobilă din viitor"
              className="h-10 w-auto transition-transform duration-300 group-hover:scale-[1.04]"
            />
          </Link>

          <nav className="hidden items-center gap-8 lg:flex" aria-label="Navigare principală">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.label.toLowerCase().replace("ă", "a")}`}
                className={({ isActive }) =>
                  `group relative font-mono text-xs uppercase tracking-[0.22em] transition-colors duration-300 ${
                    scrolled
                      ? isActive ? "text-walnut" : "text-ink/70 hover:text-ink"
                      : isActive ? "text-oak" : "text-cream/75 hover:text-cream"
                  }`
                }
              >
                {l.label}
                <span className="absolute -bottom-1.5 left-0 h-px w-0 bg-oak transition-[width] duration-300 group-hover:w-full" />
              </NavLink>
            ))}
          </nav>

          <div className="hidden items-center gap-3 lg:flex">
            <a
              href={waLink()}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-whatsapp-button"
              className={`grid h-11 w-11 place-items-center rounded-full border transition-transform duration-300 hover:-translate-y-0.5 hover:border-[#25D366] ${
                scrolled ? "border-line bg-white" : "border-cream/25 bg-cream/10"
              } text-[#128C7E]`}
              aria-label="WhatsApp"
            >
              <MessageCircle size={18} />
            </a>
            <a
              href={PHONE_TEL}
              data-testid="nav-call-button"
              className={`flex h-11 items-center gap-2 rounded-full px-6 text-sm font-semibold transition-all duration-300 hover:-translate-y-0.5 ${
                scrolled ? "bg-anthracite text-cream hover:bg-walnut" : "bg-oak text-anthracite hover:bg-cream"
              }`}
            >
              <Phone size={15} />
              0745 419 700
            </a>
          </div>

          <button
            data-testid="nav-menu-toggle"
            onClick={() => setOpen(true)}
            className={`grid h-11 w-11 place-items-center rounded-full border lg:hidden ${
              scrolled ? "border-line bg-white text-ink" : "border-cream/25 bg-cream/10 text-cream"
            }`}
            aria-label="Deschide meniul"
          >
            <Menu size={20} />
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="fixed inset-0 z-[90] flex flex-col bg-anthracite text-cream"
          >
            <div className="flex h-20 items-center justify-between px-5">
              <span className="font-serif text-xl font-bold tracking-wide">
                <img src="/assets/logo-light.png" alt="LOGITEH PROD" className="h-9 w-auto" />
              </span>
              <button
                data-testid="mobile-menu-close"
                onClick={() => setOpen(false)}
                className="grid h-11 w-11 place-items-center rounded-full border border-cream/20"
                aria-label="Închide meniul"
              >
                <X size={20} />
              </button>
            </div>
            <nav className="flex flex-1 flex-col justify-center gap-2 px-8" aria-label="Meniu mobil">
              {links.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, x: -24 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.1 + i * 0.07, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                >
                  <NavLink
                    to={l.to}
                    data-testid={`mobile-nav-link-${l.label.toLowerCase().replace("ă", "a")}`}
                    className="block border-b border-cream/10 py-5 font-serif text-4xl font-bold tracking-tight transition-colors hover:text-oak"
                  >
                    {l.label}
                  </NavLink>
                </motion.div>
              ))}
            </nav>
            <div className="flex flex-col gap-3 p-8">
              <a
                href={PHONE_TEL}
                data-testid="mobile-menu-call-button"
                className="flex h-14 items-center justify-center gap-3 rounded-full bg-oak text-base font-semibold text-anthracite"
              >
                <Phone size={18} /> Sună acum: 0745 419 700
              </a>
              <a
                href={waLink()}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="mobile-menu-whatsapp-button"
                className="flex h-14 items-center justify-center gap-3 rounded-full bg-[#25D366] text-base font-semibold text-anthracite"
              >
                <MessageCircle size={18} /> Scrie-ne pe WhatsApp
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
